'use client';

import { useCallback, useMemo, useRef } from 'react';

import { Button, Flex } from '@packages/ui';
import { Locale } from 'i18n-config';
import { useRouter } from 'next/navigation';
import { IoMdContact } from 'react-icons/io';

import ComponentDropdown, {
  IComponentDropdownOptions,
  IComponentDropdownRefProps,
} from 'components/Dropdown';
import LocaleSwitcher from 'components/LocaleSwitcher';
import { pages } from 'constants/pages';
import { IUserPermissions } from 'interfaces/permissions';
import { logoutClientRequest } from 'services/httpRequests/auth/logout/request.client';

interface IHeaderOptionsProps {
  dictionary: any;
  lang: Locale;
  permissions: IUserPermissions;
  userName: string;
}

export function HeaderOptions({
  dictionary,
  lang,
  permissions,
  userName,
}: IHeaderOptionsProps) {
  const router = useRouter();

  const componentDropdownRef = useRef<IComponentDropdownRefProps>(null);

  const handleLogoutWithForcedRedirect = useCallback(() => {
    logoutClientRequest();
    router.replace(pages.locale(lang).auth.login);
    router.refresh();
  }, [lang, router]);

  const headerDropdownOptions = useMemo(() => {
    const options: IComponentDropdownOptions[] = [
      {
        label: dictionary?.dropdown?.profile || 'Profile',
        href: pages.locale(lang).user.details,
        isVisible: permissions.canReadDetails,
      },
      {
        label: dictionary?.dropdown?.userManagement || 'User management',
        href: pages.locale(lang).user.list,
        isVisible: permissions.canRead,
      },
      {
        label: dictionary?.dropdown?.logout || 'Logout',
        href: pages.locale(lang).auth.login,
        onClick: () => handleLogoutWithForcedRedirect(),
      },
    ];

    return options;
  }, [
    dictionary?.dropdown?.logout,
    dictionary?.dropdown?.profile,
    dictionary?.dropdown?.userManagement,
    handleLogoutWithForcedRedirect,
    lang,
    permissions.canRead,
    permissions.canReadDetails,
  ]);

  return (
    <Flex className="w-fit justify-end ml-6 gap-x-2">
      <LocaleSwitcher lang={lang} />

      <Button
        className="rounded text-white"
        design="icon-only"
        onClick={() => componentDropdownRef.current?.open()}
      >
        <IoMdContact size={28} />
      </Button>

      <ComponentDropdown
        dictionary={dictionary}
        dropdownOptions={headerDropdownOptions}
        position="bottom"
        ref={componentDropdownRef}
        username={userName}
      />
    </Flex>
  );
}
